import { useState } from "react";
import { Menu, WifiOff } from "lucide-react";
import { Routes, Route, Navigate } from "react-router-dom";

import { AuthProvider, useAuth } from "./auth/AuthContext";
import { ModuleGuard, AdminGuard } from "./auth/Guard";
import Login from "./auth/Login";
import ChangePasswordForm from "./auth/ChangePasswordForm";
import { firstAllowedPath } from "./config/modules";
import { Button, Spinner } from "./components/ui";

import Sidebar from "./components/Sidebar";
import TopNavbar from "./components/TopNavbar";
import Home from "./pages/Home";
import Tools from "./pages/Tools";
import Announcements from "./pages/Announcements";
import WifiPasswords from "./pages/WifiPasswords";
import CadenceLicense from "./pages/CadenceLicense";
import Profile from "./pages/Profile";
import AdminUsers from "./pages/AdminUsers";
import BackupMonitor from "./pages/BackupMonitor";
import SoftwareMonitor from "./pages/SoftwareMonitor";

function Shell() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user } = useAuth();

  return (
    <div className="flex h-screen bg-[#F5F7FA] overflow-hidden">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile bar */}
        <div className="lg:hidden h-14 bg-white border-b border-gray-200 flex items-center justify-between px-4">
          <button onClick={() => setSidebarOpen(true)} className="p-2 -ml-2 rounded-lg hover:bg-gray-100" aria-label="Open menu">
            <Menu size={20} />
          </button>
          <span className="font-semibold text-sm text-[#111111]">LSP Employee Portal</span>
          <div className="w-8 h-8 rounded-full bg-[#C00000] text-white text-xs font-semibold flex items-center justify-center">
            {(user.fullName || user.username).charAt(0).toUpperCase()}
          </div>
        </div>

        <div className="hidden lg:block">
          <TopNavbar />
        </div>

        <main className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto">
            <Routes>
              <Route path="/" element={<ModuleGuard module="home"><Home /></ModuleGuard>} />
              <Route path="tools" element={<ModuleGuard module="tools"><Tools /></ModuleGuard>} />
              <Route path="announcements" element={<ModuleGuard module="announcements"><Announcements /></ModuleGuard>} />
              <Route path="wifi" element={<ModuleGuard module="wifi"><WifiPasswords /></ModuleGuard>} />
              <Route path="cadence-license" element={<ModuleGuard module="cadence"><CadenceLicense /></ModuleGuard>} />
              <Route path="backup" element={<ModuleGuard module="backup"><BackupMonitor /></ModuleGuard>} />
              <Route path="software" element={<ModuleGuard module="software"><SoftwareMonitor /></ModuleGuard>} />
              <Route path="admin/users" element={<AdminGuard><AdminUsers /></AdminGuard>} />
              <Route path="profile" element={<Profile />} />
              <Route path="*" element={<Navigate to={firstAllowedPath(user)} replace />} />
            </Routes>
          </div>
        </main>
      </div>
    </div>
  );
}

// First login with an admin-issued password: force the employee to choose their own.
function MustChangePassword() {
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center p-6">
      <div className="bg-white border border-gray-200 rounded-xl p-8 w-full max-w-md">
        <h1 className="text-xl font-semibold text-[#111111]">Choose a new password</h1>
        <p className="text-sm text-gray-500 mt-1 mb-6">
          Hi {user.fullName || user.username}, your administrator requires you to set a personal password before continuing.
        </p>
        <ChangePasswordForm />
        <button onClick={logout} className="mt-6 text-xs text-gray-400 hover:text-gray-700">Sign out</button>
      </div>
    </div>
  );
}

function Gate() {
  const { status, user, retry } = useAuth();

  if (status === "loading") return <div className="min-h-screen bg-[#F5F7FA]"><Spinner label="Signing you in" /></div>;

  if (status === "offline") {
    return (
      <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center p-6">
        <div className="bg-white border border-gray-200 rounded-xl p-8 max-w-sm text-center">
          <WifiOff className="mx-auto text-[#C00000]" />
          <h1 className="mt-3 font-semibold text-[#111111]">Can't reach the server</h1>
          <p className="text-sm text-gray-500 mt-1 mb-5">You're still signed in. Check your connection and try again.</p>
          <Button onClick={retry}>Retry</Button>
        </div>
      </div>
    );
  }

  if (status !== "authed") return <Login />;
  if (user.mustChangePassword) return <MustChangePassword />;
  return <Shell />;
}

export default function DashboardLayout() {
  return (
    <AuthProvider>
      <Gate />
    </AuthProvider>
  );
}
